import React, { useState, useEffect } from "react";
import PlantSelector from "../PlantSelector";
import ZoneSelector from "../ZoneSelector";
import { Box } from "@mui/material";
import TableSelector from "../TableSelector";
import ScadaSelector from "../ScadaSelector";
import PlantSelector2 from "../PlantSelector2";

interface ScadaCaseProps {
  selectedPlantId?: number | null;
  selectedZoneId?: number | null;
  selectedTableId?: number | null;
  selectedVariableId?: number | null;
  onPlantChange?: (plantId: number | null) => void;
  onZoneChange?: (zoneId: number | null) => void;
  onTableChange?: (tableId: number | null) => void;
  onVariableChange?: (
    variableId: number | null,
    variableName?: string | null,
    variableObj?: any | null,
  ) => void;
}

export default function ScadaCase({
  selectedPlantId,
  selectedZoneId,
  selectedTableId,
  selectedVariableId,
  onPlantChange,
  onZoneChange,
  onTableChange,
  onVariableChange,
}: ScadaCaseProps) {
  const [plantId, setPlantId] = useState<number | null>(
    selectedPlantId || null,
  );
  const [zoneId, setZoneId] = useState<number | null>(selectedZoneId || null);
  const [tableId, setTableId] = useState<number | null>(
    selectedTableId || null,
  );
  const [variableId, setVariableId] = useState<number | null>(
    selectedVariableId || null,
  );

  // Update local state when props change
  useEffect(() => {
    setPlantId(selectedPlantId || null);
  }, [selectedPlantId]);

  useEffect(() => {
    setZoneId(selectedZoneId || null);
  }, [selectedZoneId]);

  useEffect(() => {
    setTableId(selectedTableId || null);
  }, [selectedTableId]);

  useEffect(() => {
    setVariableId(selectedVariableId || null);
  }, [selectedVariableId]);

  // Reset zone, table and variable when plant changes to null
  useEffect(() => {
    if (plantId === null) {
      setZoneId(null);
      setTableId(null);
      setVariableId(null);
    }
  }, [plantId]);

  // Reset table and variable when zone changes to null
  useEffect(() => {
    if (zoneId === null) {
      setTableId(null);
      setVariableId(null);
    }
  }, [zoneId]);

  // Reset variable when table changes to null
  useEffect(() => {
    if (tableId === null) {
      setVariableId(null);
    }
  }, [tableId]);

  const handlePlantChange = (newPlantId: number | null) => {
    setPlantId(newPlantId);
    onPlantChange?.(newPlantId);
  };

  const handleZoneChange = (newZoneId: number | null) => {
    setZoneId(newZoneId);
    onZoneChange?.(newZoneId);
  };

  const handleTableChange = (newTableId: number | null) => {
    setTableId(newTableId);
    onTableChange?.(newTableId);
  };

  const handleVariableChange = (
    newVariableId: number | null,
    variable?: any,
  ) => {
    setVariableId(newVariableId);
    onVariableChange?.(
      newVariableId,
      variable?.variableName || null,
      variable || null,
    );
  };

  return (
    <Box sx={{ gap: 1, display: "flex", flexDirection: "column" }}>
      <PlantSelector2 value={plantId} onChange={handlePlantChange} />
      <ZoneSelector
        plantId={plantId || 1}
        value={zoneId}
        onChange={handleZoneChange}
        disabled={plantId === null}
      />
      <TableSelector
        value={tableId}
        onChange={handleTableChange}
        plantId={plantId || 1}
        zoneId={zoneId || undefined}
        disabled={plantId === null}
      />
      <ScadaSelector
        plantId={plantId || undefined}
        zoneId={zoneId || undefined}
        tableId={tableId || undefined}
        value={variableId}
        onChange={handleVariableChange}
        disabled={plantId === null}
      />
    </Box>
  );
}
